/**
 * Menu Items Missing Fiscal Setup Page
 * Lists menu items missing a fiscal_tax_category_id, ebm_item_cd, and/or
 * ebm_item_cls_cd at an outlet under RRA fiscal obligation — sourced from
 * menu_items_missing_fiscal_setup_view. Settling an order containing any of
 * these items is blocked until they're fixed here (see the settlement gate
 * in runningOrderThunks.ts). See MenuItemsConfiguredFiscalSetupPage for the
 * inverse view (already-configured items).
 */

import { useMenuItemsMissingFiscalSetup } from '@hooks/menu';
import { useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectAuthOutletId, selectEffectiveOutletId } from '@store/outlets';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { syncItemClasses } from '@/renderer/services/ebmItemClassificationService';
import { supabase } from '@/renderer/services/supabase';
import { MenuItemMissingFiscalSetup } from '@/renderer/types/menu';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import { Input, OutletIndicator } from '@components/common';
import { PaginationControls } from '@components/common/PaginationControls';
import { useDebounce } from '@hooks/useDebounce';
import { FiscalSetupRowEditor } from './FiscalSetupRowEditor';
import { SyncItemClassesButton } from './SyncItemClassesButton';

const PAGE_SIZE = 20;

interface FiscalTaxCategoryOption {
  id: number;
  code: string;
  name: string;
}

export const MenuItemsMissingFiscalSetupPage: React.FC = () => {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const authOutletId = useAppSelector(selectAuthOutletId);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [categories, setCategories] = useState<FiscalTaxCategoryOption[]>([]);

  const params = useMemo(
    () => ({ outletId, page, pageSize: PAGE_SIZE, search: debouncedSearch }),
    [outletId, page, debouncedSearch],
  );

  const { data, isLoading, isFetching, refetch } =
    useMenuItemsMissingFiscalSetup(params);

  const countryIso = currentUser?.company?.country_iso;

  useEffect(() => {
    if (!countryIso) return;
    const loadCategories = async () => {
      const { data: rows, error } = await supabase
        .from('fiscal_tax_categories')
        .select('id, code, name')
        .eq('country_iso', countryIso)
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (!error && rows) setCategories(rows as FiscalTaxCategoryOption[]);
    };
    loadCategories();
  }, [countryIso]);

  const rows: MenuItemMissingFiscalSetup[] = data?.data ?? [];
  const isCompanyWide = !outletId;

  const handleRowSaved = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.menu.missingFiscalSetup(params),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.menu.configuredFiscalSetup(params),
    });
  };

  const handleSyncItemClasses = async () => {
    if (!authOutletId) {
      apiError('Connect this device to an outlet before syncing.');
      return;
    }
    const result = await syncItemClasses(authOutletId);
    if (!result.success || !result.data) {
      apiError(result.error || 'Failed to sync item classification codes.');
      return;
    }
    apiSuccess(
      `Synced ${result.data.syncedCount} item classification code${
        result.data.syncedCount === 1 ? '' : 's'
      } from RRA.`,
    );
  };

  return (
    <div className="space-y-6">
      <OutletIndicator description="Menu items missing an EBM tax category, item code, or classification code. Settling an order containing any of these is blocked until fixed here." />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Missing Fiscal Setup
          </h1>
          <p className="text-sm text-gray-500">
            {data?.pagination.totalItems ?? 0} menu item
            {data?.pagination.totalItems === 1 ? '' : 's'} need a tax category,
            item code, or classification code
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SyncItemClassesButton onSync={handleSyncItemClasses} />
          <button
            type="button"
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <Input
          placeholder="Search by item name"
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>

      {categories.length === 0 && (
        <p className="text-sm text-amber-700">
          No active fiscal tax categories are configured for this company&apos;s
          country yet — categories must be seeded before items can be classified
          here.
        </p>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="max-h-[32rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Item
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Outlet
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Tax Category
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Item Code
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Classification Code
                </th>
                <th className="px-5 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={isCompanyWide ? 6 : 5}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={isCompanyWide ? 6 : 5}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Every menu item has a tax category, item code, and
                    classification code. Nothing to fix here.
                  </td>
                </tr>
              )}
              {rows.map((item) => (
                <FiscalSetupRowEditor
                  key={item.menu_item_id}
                  item={item}
                  categories={categories}
                  onSaved={handleRowSaved}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data && (
        <PaginationControls
          pagination={data.pagination}
          itemLabel="items"
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default MenuItemsMissingFiscalSetupPage;
